import React, { useContext, useEffect, useState, createContext } from "react";

// Utils
import { AuthContext } from "../context/AuthContext";
import { useSocket } from "../hooks/useSocket";
import { ChatContext } from "./chat/ChatContext";
import { types } from "../types/types";
import { fetchWithToken } from "../api/fetch";

// Assets
import newMessage from "../assets/sound/new_message.mp3";
import resp from "../assets/sound/notificacion.mp3";

export const SocketContext = createContext();

/**
 * Proveedor del contexto del socket
 *
 * @param {object} children - Componentes hijos
 * @returns {object}
 */
export const SocketProvider = ({ children }) => {
  let [audio, setAudio] = useState(new Audio(resp)); // Sonido de notificación
  let [audiose, setAudiose] = useState(new Audio(newMessage)); // Sonido de nuevo mensaje
  const [agentConfig, setAgentConfig] = useState(null); // Configuración del agente

  const { auth } = useContext(AuthContext); // Autenticación del usuario logueado
  const { dispatch } = useContext(ChatContext); // Información del chat

  const REACT_APP_API = process.env.REACT_APP_API;

  const uid = auth.uid;

  // Socket
  const { socket, online, conectarSocket, desconectarSocket } =
    useSocket(REACT_APP_API);

  // Conectar socket
  useEffect(() => {
    if (auth.logged) {
      conectarSocket();
    }
  }, [auth, conectarSocket]);

  // Desconectar socket
  useEffect(() => {
    if (!auth.logged) {
      desconectarSocket();
    }
  }, [auth, desconectarSocket]);

  // Cargar roles
  useEffect(() => {
    socket?.on("roles", async (roles) => {
      dispatch({
        type: types.loadRoles,
        payload: roles,
      });
    });
  }, [socket, dispatch]);

  // Cargar agentes
  useEffect(() => {
    socket?.on("lista-agentes", async (agentes) => {
      dispatch({
        type: types.loadAgentes,
        payload: agentes,
      });
    });
  }, [socket, dispatch]);

  // Cargar etiquetas
  useEffect(() => {
    socket?.on("etiquetas", async (marks) => {
      dispatch({
        type: types.cargarMarks,
        payload: marks,
      });
    });
  }, [socket, dispatch]);

  // Mensajes del bot
  useEffect(() => {
    socket?.on("getTotalBotMessages", async (totalBotMessages) => {
      dispatch({
        type: types.nuevosMensajesBot,
        payload: totalBotMessages,
      });
    });
  }, [socket, dispatch]);

  // Mensajes de agentes
  useEffect(() => {
    socket?.on("getTotalAgentMessages", async (totalAgentMessages) => {
      dispatch({
        type: types.totalAgentMessages,
        payload: totalAgentMessages,
      });
    });
  }, [socket, dispatch]);

  // Mensajes no leidos
  useEffect(() => {
    socket?.on("getUnreadMessages", async (unreadMessages) => {
      dispatch({
        type: types.unreadMessages,
        payload: unreadMessages,
      });
    });
  }, [socket, dispatch]);

  // Chats en espera
  useEffect(() => {
    socket?.on("getChatOnHold", async (chatOnHold) => {
      dispatch({
        type: types.chatOnHold,
        payload: chatOnHold,
      });
    });
  }, [socket, dispatch]);

  // Estadisticas de Gupshup
  useEffect(() => {
    socket?.on("getEstDash", async (estDash) => {
      dispatch({
        type: types.estDash,
        payload: estDash,
      });
    });
  }, [socket, dispatch]);

  // Estadisticas de agentes
  useEffect(() => {
    socket?.on("getAgentStats", async (agentStats) => {
      dispatch({
        type: types.nuevasEstadisticas,
        payload: agentStats,
      });
    });
  }, [socket, dispatch]);

  // Lista de respuestas
  useEffect(() => {
    socket?.on("lista-respuestas", async (answers) => {
      dispatch({
        type: types.cargarRespuestas,
        payload: answers,
      });
    });
  }, [socket, dispatch]);
  // CHAT USER  MOVE
  useEffect(() => {
    socket?.on("movedchat", async ({ chatId, from, to }) => {
      dispatch({
        type: types.moveChatToCategory,
        payload: { chatId, from, to },
      });
    });
  }, [socket, dispatch]);

  // Cargar configuracion del agente
  const fetchAgentConfig = async (uid) => {
    try {
      const data = await fetchWithToken(
        "ajustes/getconfig",
        { id_agent: uid },
        "POST"
      );

      const incomingSound = new Audio(
        REACT_APP_API + "/" + data.data.incoming_path
      );
      const outgoingSound = new Audio(
        REACT_APP_API + "/" + data.data.outgoing_path
      );

      setAgentConfig(data.data);
      setAudio(incomingSound);
      setAudiose(outgoingSound);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (uid) {
      fetchAgentConfig(uid);
    }
  }, [uid]);

  // Mensajes del chat recibidos
  useEffect(() => {
    socket?.on("mensaje-front", (mensaje) => {
      const { status } = mensaje;

      if (agentConfig) {
        // Reproducir sonido según la configuración del agente
        status === "res" ? audio.play() : audiose.play();
      }
    });
  }, [socket, audio, audiose, agentConfig]);

  return (
    <SocketContext.Provider value={{ socket, online }}>
      {children}
    </SocketContext.Provider>
  );
};
